import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  LocalShipping as LogoIcon,
} from '@mui/icons-material'
import { useAuthStore } from '../store/authStore'
import { authService } from '../api/authService'
import { useEnumStore } from '../store/enumStore'
import { metadataService } from '../api/adminServices'
import {
  hasStoredLanguagePreference,
  useSettingsStore,
  type Language,
} from '../store/settingsStore'

export default function LoginPage() {
  const theme = useTheme()
  const { t } = useTranslation()
  const setLanguage = useSettingsStore((s) => s.setLanguage)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const { setEnums, setError: setEnumError } = useEnumStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authService.login({ username, password })

      // Check if user has SYSTEM_ADMIN role
      if (!response.user.roles.includes('SYSTEM_ADMIN')) {
        setError(t('login.accessDenied'))
        setLoading(false)
        return
      }

      setAuth(response.accessToken, response.refreshToken, response.user)

      const userLocale = (response.user.locale || 'EN') as Language
      // Local device preference wins; seed from server only before the user customizes settings.
      if (!hasStoredLanguagePreference()) {
        setLanguage(userLocale)
      }

      // Enum labels follow the user's server locale (JWT), like Driver/Shipper apps
      try {
        const enums = await metadataService.getEnums()
        setEnums(enums, userLocale)
      } catch (enumError: any) {
        const enumMessage =
          enumError?.response?.data?.message ||
          enumError?.message ||
          'Failed to load metadata enums'
        console.error('Failed to load enums:', enumError)
        setEnumError(enumMessage)
        // Continue even if enum loading fails
      }
      
      navigate('/')
    } catch (err: any) {
      const errorMessage =
        (typeof err.response?.data?.message === 'string' && err.response.data.message.trim()) ||
        (typeof err.message === 'string' && err.message.trim()) ||
        t('login.loginFailed')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha('#6366f1', 0.05)} 0%, ${alpha('#ec4899', 0.05)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha('#6366f1', 0.1)} 0%, ${alpha('#8b5cf6', 0.05)} 100%)`,
          filter: 'blur(80px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha('#ec4899', 0.1)} 0%, ${alpha('#f472b6', 0.05)} 100%)`,
          filter: 'blur(80px)',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Logo & Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
              }}
            >
              <LogoIcon sx={{ color: 'white', fontSize: 36 }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              LogarGo
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t('login.subtitle')}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              id="username"
              name="username"
              label={t('login.emailLabel')}
              placeholder={t('login.emailPlaceholder')}
              autoComplete="email"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              fullWidth
              id="password"
              name="password"
              label={t('login.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !username || !password}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {t('login.signingIn')}
                </Box>
              ) : (
                t('login.signIn')
              )}
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('login.requiresAdmin')}
            </Typography>
          </Box>
        </Paper>

        {/* Copyright */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 3 }}
        >
          {t('login.copyright', { year: new Date().getFullYear() })}
        </Typography>
      </Container>
    </Box>
  )
}
